import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useFetchCompanyInfosQuery } from "../../Apis/companyApi";
import { useFetchChSummaryQuery } from "../../Apis/chAnalyticsApi";
import { useOnTimeBufferMinutes } from "../../Hooks/useOnTimeBufferMinutes";
import { useTimeframeSelector } from "../../Hooks/useTimeframeSelector";
import ChFilterRow from "./ChFilterRow";
import ChSummaryCards from "./ChSummaryCards";
import ChBreakdownTable from "./ChBreakdownTable";
import ChDrilldownDrawer from "./ChDrilldownDrawer";
import styles from "./chAnalytics.module.css";

export const CH_ANALYTICS_COMPONENT = "CH_ANALYTICS_COMPONENT";

const ChAnalyticsWrapper = () => {
    const { data: enterpriseAccounts } = useFetchCompanyInfosQuery();

    const [selectedLocations, setSelectedLocations] = useState([]);
    const [drawerState, setDrawerState] = useState({
        isOpen: false,
        companyId: null,
        companyName: "",
        metric: null,
        page: 1,
    });

    const {
        timeframe,
        dateRange,
        startDate,
        endDate,
        onTimeframeChange,
        onRangeChange,
        onRangeClear,
    } = useTimeframeSelector("thisMonth", {
        onReset: useCallback(() => setDrawerState((prev) => ({ ...prev, isOpen: false })), []),
    });

    const companyList = useMemo(() => {
        if (!enterpriseAccounts) return [];
        return enterpriseAccounts.getAccountsArray().map((c) => ({
            id: c.companyId,
            name: c.companyName,
        }));
    }, [enterpriseAccounts]);

    useEffect(() => {
        if (companyList.length > 0 && selectedLocations.length === 0) {
            setSelectedLocations(companyList.map((c) => c.id));
        }
    }, [companyList]); // eslint-disable-line react-hooks/exhaustive-deps

    const onTimeBufferMinutes = useOnTimeBufferMinutes();

    const { data: summaryData, isFetching: summaryLoading } = useFetchChSummaryQuery(
        { timeframe, startDate, endDate, onTimeBufferMinutes },
        { skip: !timeframe || (timeframe === "range" && (!startDate || !endDate)) }
    );

    const accountsMap = useMemo(() => {
        if (!enterpriseAccounts) return {};
        return enterpriseAccounts.getAccountsObj();
    }, [enterpriseAccounts]);

    const filteredCompanies = useMemo(() => {
        if (!summaryData) return [];
        const companies = summaryData.getCompanies();
        const locationSet = new Set(selectedLocations);
        return companies
            .filter((c) => locationSet.has(c.companyId))
            .map((c) => ({
                ...c,
                companyName: accountsMap[c.companyId]?.companyName ?? `Company ${c.companyId}`,
            }));
    }, [summaryData, selectedLocations, accountsMap]);

    const breakdownRows = useMemo(() => {
        return filteredCompanies.map((c) => ({
            companyId: c.companyId,
            companyName: c.companyName,
            totalCreated: c.totalCreated,
            totalDelivered: c.totalDelivered,
            totalFailed: c.totalFailed,
            totalIncomplete: c.totalIncomplete,
            totalDeleted: c.totalDeleted,
            deliveredByDriver: c.deliveredByDriver,
            deliveredByDispatcher: c.deliveredByDispatcher,
            ontimePercentage: c.ontimePercentage,
            avgDriveTimeMinutes: c.avgDriveTimeMinutes,
            avgDeliveryTimeMinutes: c.avgDeliveryTimeMinutes,
        }));
    }, [filteredCompanies]);

    const handleCellClick = useCallback(({ companyId, companyName, metric }) => {
        setDrawerState({ isOpen: true, companyId, companyName, metric, page: 1 });
    }, []);

    const handleDrawerClose = useCallback(() => {
        setDrawerState((prev) => ({ ...prev, isOpen: false }));
    }, []);

    const handleDrawerPageChange = useCallback((page) => {
        setDrawerState((prev) => ({ ...prev, page }));
    }, []);

    const handleLocationsChange = useCallback((locations) => {
        setSelectedLocations(locations);
        setDrawerState((prev) => ({ ...prev, isOpen: false }));
    }, []);

    const handleExport = useCallback(() => {
        if (filteredCompanies.length === 0) return;

        function esc(v) {
            const s = v == null ? "" : String(v);
            return s.includes(",") || s.includes('"') || s.includes("\n")
                ? `"${s.replace(/"/g, '""')}"` : s;
        }

        const totals = filteredCompanies.reduce(
            (acc, c) => ({
                totalCreated: acc.totalCreated + c.totalCreated,
                totalDelivered: acc.totalDelivered + c.totalDelivered,
                deliveredByDriver: acc.deliveredByDriver + c.deliveredByDriver,
                deliveredByDispatcher: acc.deliveredByDispatcher + c.deliveredByDispatcher,
                totalFailed: acc.totalFailed + c.totalFailed,
                totalIncomplete: acc.totalIncomplete + c.totalIncomplete,
                totalDeleted: acc.totalDeleted + c.totalDeleted,
                _wOntime: acc._wOntime + c.ontimePercentage * c.totalDelivered,
                _wDrive: acc._wDrive + c.avgDriveTimeMinutes * c.totalDelivered,
                _wDeliv: acc._wDeliv + c.avgDeliveryTimeMinutes * c.totalDelivered,
            }),
            { totalCreated: 0, totalDelivered: 0, deliveredByDriver: 0, deliveredByDispatcher: 0, totalFailed: 0, totalIncomplete: 0, totalDeleted: 0, _wOntime: 0, _wDrive: 0, _wDeliv: 0 }
        );
        const d = totals.totalDelivered || 1;
        const summary = {
            ...totals,
            ontimePercentage: totals._wOntime / d,
            avgDriveTimeMinutes: totals._wDrive / d,
            avgDeliveryTimeMinutes: totals._wDeliv / d,
        };

        const summaryHeaders = ["Created", "Delivered", "Delivered by Driver", "Delivered by Dispatcher", "Failed", "Incomplete", "Deleted", "% On-time", "Avg. on the way (min)", "Avg. placement to delivery (min)"];
        const summaryRow = [
            summary.totalCreated,
            summary.totalDelivered,
            summary.deliveredByDriver,
            summary.deliveredByDispatcher,
            summary.totalFailed,
            summary.totalIncomplete,
            summary.totalDeleted,
            `${summary.ontimePercentage.toFixed(1)}%`,
            summary.avgDriveTimeMinutes.toFixed(0),
            summary.avgDeliveryTimeMinutes.toFixed(0),
        ];

        const breakdownHeaders = ["Company", "Created", "Delivered", "Failed", "Incomplete", "Deleted", "% On-time", "Avg. on the way (min)"];
        const breakdownRowLines = breakdownRows.map((r) => [
            r.companyName,
            r.totalCreated,
            r.totalDelivered,
            r.totalFailed,
            r.totalIncomplete,
            r.totalDeleted,
            `${r.ontimePercentage.toFixed(1)}%`,
            r.avgDriveTimeMinutes.toFixed(0),
        ].map(esc).join(","));

        const tfLabel = startDate && endDate ? `${startDate}_${endDate}` : timeframe;
        const today = new Date().toISOString().slice(0, 10);
        const filename = `analytics_${tfLabel}_${today}.csv`;

        const csv = [
            "Summary",
            summaryHeaders.map(esc).join(","),
            summaryRow.map(esc).join(","),
            "",
            "Breakdown by Companies",
            breakdownHeaders.map(esc).join(","),
            ...breakdownRowLines,
        ].join("\r\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [filteredCompanies, breakdownRows, timeframe, startDate, endDate]);

    return (
        <div className={styles.chRoot}>
            <ChFilterRow
                companyList={companyList}
                selectedLocations={selectedLocations}
                onLocationsChange={handleLocationsChange}
                timeframe={timeframe}
                onTimeframeChange={onTimeframeChange}
                dateRange={dateRange}
                onRangeChange={onRangeChange}
                onRangeClear={onRangeClear}
            />
            <ChSummaryCards
                companies={filteredCompanies}
                loading={summaryLoading}
            />
            <ChBreakdownTable
                rows={breakdownRows}
                loading={summaryLoading}
                activeDrawer={drawerState.isOpen ? drawerState : null}
                onCellClick={handleCellClick}
                onExport={handleExport}
            />
            <ChDrilldownDrawer
                drawerState={drawerState}
                timeframe={timeframe}
                startDate={startDate}
                endDate={endDate}
                dateRange={dateRange}
                onClose={handleDrawerClose}
                onPageChange={handleDrawerPageChange}
            />
        </div>
    );
};

export default ChAnalyticsWrapper;
